import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Chip,
  Tooltip,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import { EditableDataGrid, createColumns } from '../components/EditableDataGrid';
import { StageProcessor } from '../components/StageProcessor';
import { mapNAICSCodes } from '../api/stageFunctions';
import { ProductWithPSC, ProductWithCodes } from '../types/pipeline';
import { useStageCache } from '../hooks/useStageCache';
import { STAGE_NUMBERS } from '../utils/cacheUtils';
import { getButtonText } from '../utils/cacheUIHelpers';

interface NAICSCodeMappingProps {
  products: ProductWithPSC[];
  onComplete: (products: ProductWithCodes[]) => void;
  flowId?: string | null;
}

const NAICSCodeMapping: React.FC<NAICSCodeMappingProps> = ({
  products,
  onComplete,
  flowId,
}) => {
  const [productsWithCodes, setProductsWithCodes] = useState<ProductWithCodes[]>([]);
  const [triggerMapping, setTriggerMapping] = useState(0);
  const [useCachedData, setUseCachedData] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);
  
  // Check cache for this stage
  const { cachedData, isCacheValid, isCheckingCache, refreshCache, checkCacheManually, validateCacheWithInput } = useStageCache({
    flowId,
    stageNumber: STAGE_NUMBERS.NAICS,
    inputData: { products },
    enabled: useCachedData,
  });

  // Validate cache only once per distinct input (flowId + products snapshot)
  const validatedOnceRef = useRef<boolean>(false);
  useEffect(() => {
    if (flowId && useCachedData && !validatedOnceRef.current) {
      validatedOnceRef.current = true;
      validateCacheWithInput();
    }
  }, [flowId, useCachedData, validateCacheWithInput]);

  // Default checkbox to match cache availability (disabled when no cache)
  useEffect(() => {
    if (!isCheckingCache) {
      setUseCachedData(isCacheValid);
    }
  }, [isCheckingCache, isCacheValid]);

  // Auto-map on mount - always trigger at least once
  useEffect(() => {
    // Wait for cache check to complete
    if (!isCheckingCache && !hasInitialized) {
      setHasInitialized(true);
      // Always trigger at least once to let StageProcessor handle cached or fresh data
      handleMapNAICS();
    }
  }, [isCheckingCache, hasInitialized]);
  
  // Load cached data when available
  useEffect(() => {
    if (isCacheValid && cachedData && useCachedData && productsWithCodes.length === 0) {
      // Check if cachedData is the array directly or wrapped in an object
      const products = Array.isArray(cachedData) 
        ? cachedData 
        : (cachedData.products_with_naics || cachedData.products || []);
      
      if (Array.isArray(products) && products.length > 0) {
        handleNAICSMapped(products);
      }
    }
  }, [isCacheValid, cachedData, useCachedData, productsWithCodes.length]);

  const handleMapNAICS = () => {
    setTriggerMapping(prev => prev + 1);
  };

  const handleNAICSMapped = (data: any) => {
    // With UI-ready format, products are consistently formatted
    const products = data.productsWithNAICS || data.products || data;
    
    if (!Array.isArray(products)) {
      console.error('NAICSCodeMapping: Expected products array but got:', data);
      return;
    }
    
    // UI-ready format already has all required fields - no transformation needed
    setProductsWithCodes(products);
  };


  const handleComplete = () => {
    onComplete(productsWithCodes);
  };

  // Define columns for NAICS mapping grid - cumulative view with actual data
  const naicsColumns = [
    {
      field: 'product_name',
      headerName: 'Product Name',
      width: 280,  // Increased from 200
      valueGetter: (params: any) => {
        return params.row.product_name || params.row.name || 'Unknown Product';
      },
    },
    createColumns.text('manufacturer', 'Manufacturer', 180),  // Increased from 130
    createColumns.text('model', 'Model', 150),  // Increased from 100
    {
      field: 'pscCode',
      headerName: 'PSC',
      width: 100,  // Increased from 80
      valueGetter: (params: any) => {
        // Get primary PSC from various possible locations
        const primaryPSC = params.row.psc_codes?.[0]?.code || 
                          params.row.psc_codes?.[0] ||
                          params.row.pscCode || '';
        let pscCode = typeof primaryPSC === 'object' ? primaryPSC.code : primaryPSC;
        
        // Handle case where code contains description (e.g., "4240: Safety...")
        if (typeof pscCode === 'string' && pscCode.includes(':')) {
          pscCode = pscCode.split(':')[0].trim();
        }
        
        return pscCode;
      },
      renderCell: (params: any) => {
        // Get the raw PSC data to extract description
        const pscData = params.row.psc_codes?.[0];
        let description = '';
        
        if (typeof pscData === 'object') {
          // First check if description is in the description field
          if (pscData.description && pscData.description.trim()) {
            description = pscData.description;
          } 
          // If description field is empty, check if it's embedded in the code field
          else if (pscData.code && typeof pscData.code === 'string' && pscData.code.includes(':')) {
            const parts = pscData.code.split(':');
            description = parts.slice(1).join(':').trim();
          }
        } else if (typeof pscData === 'string' && pscData.includes(':')) {
          // Extract description from "4240: Safety..." format in string
          const parts = pscData.split(':');
          description = parts.slice(1).join(':').trim();
        } 
        // Fallback to pscDescription field if available
        else if (params.row.pscDescription) {
          description = params.row.pscDescription;
        }
        // If no description found, don't show redundant tooltip
        
        const chip = (
          <Chip 
            label={params.value} 
            size="small" 
            color="secondary"
            variant="outlined"
          />
        );
        
        // Only wrap in tooltip if we have a meaningful description
        if (description && description.trim() && description !== params.value) {
          return (
            <Tooltip title={description}>
              {chip}
            </Tooltip>
          );
        }
        return chip;
      },
    },
    {
      field: 'naics_from_psc',
      headerName: 'NAICS Codes',
      width: 350,  // Increased width to better fill the space
      renderCell: (params: any) => {
        const naicsCodes = params.value || [];
        const naicsWithDesc = params.row.naics_with_descriptions || [];
        
        if (naicsCodes.length === 0) {
          return <Typography variant="body2" color="text.secondary">No NAICS found</Typography>;
        }
        
        // Create a map of code to description if available
        const descriptionMap: Record<string, string> = {};
        naicsWithDesc.forEach((item: any) => {
          if (item.code && item.description) {
            descriptionMap[item.code] = item.description;
          }
        });
        
        return (
          <Box display="flex" gap={0.5} flexWrap="wrap">
            {naicsCodes.map((code: string, idx: number) => {
              const description = descriptionMap[code];
              const chip = (
                <Chip 
                  key={idx}
                  label={code} 
                  size="small" 
                  color={idx === 0 ? "primary" : "default"}
                  variant={idx === 0 ? "filled" : "outlined"}
                  sx={{ cursor: description ? 'help' : 'default' }}
                />
              );
              
              // Only wrap in tooltip if we have a description
              if (description) {
                return (
                  <Tooltip
                    key={idx}
                    title={description}
                    placement="top"
                    arrow
                  >
                    {chip}
                  </Tooltip>
                );
              }
              return chip;
            })}
          </Box>
        );
      },
    },
  ];

  // Calculate statistics
  const stats = {
    total: productsWithCodes.length,
    withNAICS: productsWithCodes.filter(p => p.naics_from_psc && p.naics_from_psc.length > 0).length,
    uniqueNAICS: new Set(
      productsWithCodes.flatMap(p => p.naics_from_psc || [])
    ).size,
    totalContractValue: productsWithCodes.reduce((sum: number, p: any) => {
      const amountStr = p.specifications?.award_amount || '$0';
      const amount = parseFloat(amountStr.replace(/[$,]/g, '')) || 0;
      return sum + amount;
    }, 0),
  };

  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          <BusinessIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
          NAICS Code Mapping
        </Typography>
        <Typography variant="body2" color="text.secondary">
          North American Industry Classification System (NAICS) codes identify the primary business 
          of vendors. These codes are mapped from PSC codes and used to find appropriate vendors.
        </Typography>
      </Paper>

      {/* Process NAICS Mapping */}
      {/* Cache Control and Mapping Button */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={useCachedData}
                onChange={(e) => setUseCachedData(e.target.checked)}
                color="primary"
                disabled={isCheckingCache || !isCacheValid}
              />
            }
            label="Use cached data when available"
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<BusinessIcon />}
            onClick={() => setTriggerMapping(prev => prev + 1)}
            disabled={isCheckingCache}
          >
            {getButtonText('Map NAICS Codes', useCachedData, cachedData && cachedData.products_with_naics)}
          </Button>
        </Box>
      </Paper>
      
      <StageProcessor
        title="Mapping NAICS Codes"
        description="Analyzing PSC codes to identify relevant industry classifications..."
        processFunction={() => mapNAICSCodes(products, flowId, !useCachedData)}
        onSuccess={handleNAICSMapped}
        trigger={triggerMapping}
        autoProcess={false}
        showStatus={true}
      >
        {(data, status) => {
          if (!data || productsWithCodes.length === 0) return null;

          return (
            <>
              {/* Statistics */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                  <Typography variant="subtitle1" fontWeight="bold">
                    Mapping Summary:
                  </Typography>
                  <Chip 
                    label={`${stats.total} Products`} 
                    color="primary" 
                  />
                  <Chip 
                    label={`${stats.withNAICS} with NAICS`} 
                    color="info"
                  />
                  <Chip 
                    label={`${stats.uniqueNAICS} Unique NAICS`} 
                    color="secondary"
                    variant="outlined"
                  />
                </Box>
              </Paper>

              {/* Info Alert */}
              <Alert severity="info" sx={{ mb: 2 }}>
                NAICS codes have been mapped from PSC codes using historical contract data. 
                These codes identify the primary business classifications of potential vendors.
              </Alert>

              {/* Cumulative Data Grid */}
              <Paper sx={{ p: 2 }}>
                
                <EditableDataGrid
                  data={productsWithCodes}
                  columns={naicsColumns}
                  onDataChange={setProductsWithCodes}
                  onAddRow={() => {
                    const newProduct: any = {
                      id: `custom-${Date.now()}`,
                      name: 'New Product',
                      manufacturer: 'Custom',
                      model: '',
                      specifications: [],
                      price: '0',
                      gsaSchedule: false,
                      contractVehicle: 'Open Market',
                      leadTime: 30,
                      pscCode: '7010',
                      pscDescription: 'ADP Central Processing Unit',
                      naicsCode: '541512',
                      naicsDescription: 'Computer Systems Design Services',
                      alternativeNAICS: [],
                      mappingConfidence: 50,
                    };
                    return newProduct;
                  }}
                  height={500}
                  showToolbar={true}
                  selectable={false}
                  customActions={
                    <Button
                      variant="outlined"
                      startIcon={<BusinessIcon />}
                      onClick={handleMapNAICS}
                    >
                      Re-map NAICS
                    </Button>
                  }
                />

                {/* Action Buttons */}
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    NAICS codes will be used to search for qualified vendors
                  </Typography>
                  <Box>
                    {/* JSON export removed for non-technical users */}
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleComplete}
                    >
                      Continue to Vendor Discovery
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </>
          );
        }}
      </StageProcessor>
    </Box>
  );
};

export default NAICSCodeMapping;
